import { AnimatePresence, motion } from "framer-motion";
import { Bike, Car, KeyRound, Luggage, Truck, Wifi, Signal, BatteryFull } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import { IMAGES } from "@/data/products";

/**
 * Premium hero visual: Tracksy magnetic puck floating next to a phone
 * running the active platform's Find network with multiple tracked items
 * pinned on the map. Everything is pure SVG + framer-motion so the
 * Apple ↔ Android swap is a buttery cross-fade.
 */

// Tracked assets shown as pins on the phone's map. Positions are percentages
// of the phone screen's inner box.
const PINS = [
  { id: "car", label: "Toyota Hilux", loc: "Buderim", icon: Car, x: 32, y: 28, hero: true },
  { id: "trailer", label: "Box Trailer", loc: "Workshop", icon: Truck, x: 68, y: 44 },
  { id: "bike", label: "Trek Bike", loc: "Garage", icon: Bike, x: 20, y: 62 },
  { id: "keys", label: "House Keys", loc: "Hallway", icon: KeyRound, x: 54, y: 70 },
  { id: "lug", label: "Carry-on", loc: "Hotel", icon: Luggage, x: 78, y: 20 },
];

// Apple-style map palette (warm, light, Apple Maps-esque)
const APPLE_MAP = {
  bg: "#F3EEE3",
  land: "#E8DECB",
  park: "#C9DBB2",
  road: "#FFFFFF",
  roadStroke: "#D7CBB2",
  water: "#A6D1EA",
  statusBg: "#000000",
  chip: "#FFFFFF",
};

// Android-style map palette (Material 3, cool blues)
const ANDROID_MAP = {
  bg: "#E8EEF5",
  land: "#DDE6F0",
  park: "#CFE4C2",
  road: "#FFFFFF",
  roadStroke: "#BBC9DA",
  water: "#AAD3F2",
  statusBg: "#0B57D0",
  chip: "#FFFFFF",
};

const MapSvg = ({ palette }) => (
  <svg viewBox="0 0 300 480" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
    <rect width="300" height="480" fill={palette.bg} />
    {/* Land blobs */}
    <path d="M-20,80 C 40,40 120,120 200,80 C 260,55 320,100 320,160 L 320,520 L -20,520 Z"
          fill={palette.land} />
    {/* Park */}
    <path d="M30,260 C 70,230 130,270 160,250 C 190,240 210,290 180,320 C 150,350 70,340 40,320 Z"
          fill={palette.park} opacity="0.9" />
    {/* Water */}
    <path d="M200,340 C 240,320 300,360 320,400 L 320,520 L 180,520 C 170,460 180,380 200,340 Z"
          fill={palette.water} opacity="0.85" />
    {/* Roads */}
    <g stroke={palette.roadStroke} strokeWidth="0.6" fill="none">
      <path d="M0,170 C 80,160 180,220 320,210" stroke={palette.road} strokeWidth="10" />
      <path d="M0,170 C 80,160 180,220 320,210" strokeWidth="12" />
      <path d="M60,0 C 70,120 140,220 150,360 C 155,420 170,480 160,520" stroke={palette.road} strokeWidth="8" />
      <path d="M60,0 C 70,120 140,220 150,360 C 155,420 170,480 160,520" strokeWidth="10" />
      <path d="M240,20 C 230,120 250,200 260,340" stroke={palette.road} strokeWidth="6" />
      <path d="M240,20 C 230,120 250,200 260,340" strokeWidth="8" />
      <path d="M0,380 C 60,370 120,400 220,390" stroke={palette.road} strokeWidth="6" />
      <path d="M0,380 C 60,370 120,400 220,390" strokeWidth="8" />
    </g>
  </svg>
);

const Phone = ({ platform }) => {
  const isAndroid = platform === "android";
  const palette = isAndroid ? ANDROID_MAP : APPLE_MAP;
  const accent = isAndroid ? "#1A73E8" : "#FF6B00";

  return (
    <div className="relative mx-auto h-[520px] w-[260px] sm:h-[560px] sm:w-[280px]">
      {/* Phone body — Android (Pixel 8) has flatter edges + brushed aluminium rim, iPhone has titanium rim */}
      <div
        className={`relative h-full w-full p-[6px] shadow-[0_30px_80px_rgba(29,29,31,0.28),0_8px_30px_rgba(29,29,31,0.18)] ${
          isAndroid
            ? "rounded-[36px] bg-gradient-to-b from-[#4a4b50] via-[#2a2b2e] to-[#17181a]"
            : "rounded-[52px] bg-gradient-to-b from-[#3a3a3f] via-[#1d1d1f] to-[#0a0a0b]"
        }`}
      >
        {/* Thin brushed-metal highlight along the rim for Pixel realism */}
        {isAndroid && (
          <>
            <span className="pointer-events-none absolute inset-x-6 top-0 h-[2px] rounded-full bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            <span className="pointer-events-none absolute inset-y-10 left-0 w-[2px] rounded-full bg-gradient-to-b from-transparent via-white/10 to-transparent" />
            <span className="pointer-events-none absolute inset-y-10 right-0 w-[2px] rounded-full bg-gradient-to-b from-transparent via-white/10 to-transparent" />
          </>
        )}
        {/* Inner glass bezel */}
        <div className={`relative h-full w-full overflow-hidden bg-black ${isAndroid ? "rounded-[32px]" : "rounded-[46px]"}`}>
          {/* iOS-style status bar overlay */}
          {!isAndroid && (
            <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 pt-3 text-[11px] font-bold text-white">
              <span className="tracking-tight">9:41</span>
              <span className="flex items-center gap-1 opacity-90">
                <Signal className="h-2.5 w-3" />
                <Wifi className="h-2.5 w-2.5" />
                <BatteryFull className="h-3 w-5" />
              </span>
            </div>
          )}

          {/* iPhone Dynamic Island */}
          {!isAndroid && (
            <div className="absolute left-1/2 top-2 z-30 h-7 w-28 -translate-x-1/2 rounded-full bg-black" />
          )}

          {/* Android status bar — compact Material status strip */}
          {isAndroid && (
            <div
              className="absolute inset-x-0 top-0 z-20 flex h-6 items-center justify-between px-4 text-[10px] font-bold text-white"
              style={{ background: palette.statusBg }}
            >
              <span>9:41</span>
              <span className="opacity-90 inline-flex items-center gap-1">
                <Signal className="h-2.5 w-3" />
                <Wifi className="h-2.5 w-2.5" />
                <BatteryFull className="h-3 w-5" />
              </span>
            </div>
          )}

          {/* Pixel punch-hole — centered, slightly below status bar */}
          {isAndroid && (
            <div className="absolute left-1/2 top-[12px] z-30 h-3.5 w-3.5 -translate-x-1/2 rounded-full bg-black ring-[2px] ring-neutral-700" />
          )}

          {/* Map canvas — offset below status bar + Dynamic Island / punch-hole */}
          <div className="absolute inset-0 top-10 sm:top-9">
            <MapSvg palette={palette} />

            {/* App title bar — nudged down so it never covers the Dynamic Island / punch-hole */}
            <motion.div
              className="absolute left-3 right-3 top-5 flex items-center justify-between rounded-xl bg-white/95 px-3 py-1.5 shadow-sm backdrop-blur sm:top-3"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15 }}
            >
              <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>
                {isAndroid ? "Find Hub" : "Find My"}
              </span>
              <span className="text-[9px] font-bold text-neutral-500">All items</span>
            </motion.div>

            {/* Pins */}
            {PINS.map((pin, i) => (
              <motion.div
                key={pin.id}
                className="absolute"
                style={{ left: `${pin.x}%`, top: `${pin.y}%`, transform: "translate(-50%,-100%)" }}
                initial={{ opacity: 0, scale: 0.4, y: -12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.3 + i * 0.08, type: "spring", stiffness: 220, damping: 18 }}
              >
                <motion.span
                  className="absolute left-1/2 top-full h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ background: accent, opacity: 0.25 }}
                  animate={{ scale: [1, 2, 1], opacity: [0.25, 0, 0.25] }}
                  transition={{ duration: 2 + (i % 2) * 0.4, repeat: Infinity, delay: i * 0.4 }}
                />
                <div
                  className="relative flex h-7 w-7 items-center justify-center rounded-full shadow-md ring-2 ring-white"
                  style={{ background: pin.hero ? accent : "#FFFFFF" }}
                >
                  <pin.icon className="h-3.5 w-3.5" style={{ color: pin.hero ? "#FFFFFF" : accent }} />
                </div>
                {pin.hero && (
                  <motion.div
                    className="absolute left-1/2 top-[-56px] w-max -translate-x-1/2 rounded-xl bg-white px-2 py-1 text-left shadow-lg ring-1 ring-black/5"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.65 }}
                  >
                    <p className="text-[9px] font-black uppercase tracking-widest" style={{ color: accent }}>{pin.label}</p>
                    <p className="text-[9px] text-neutral-500">{pin.loc} · 2 min ago</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Bottom card — rounded like a Material / iOS sheet */}
          <motion.div
            className={`absolute inset-x-3 bottom-3 bg-white/97 p-3 shadow-lg backdrop-blur ${
              isAndroid ? "rounded-xl" : "rounded-2xl"
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>Items</p>
              <p className="text-[9px] font-bold text-neutral-500">{PINS.length} tracked</p>
            </div>
            <ul className="space-y-1.5">
              {PINS.slice(0, 3).map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ background: `${accent}22` }}>
                    <p.icon className="h-3 w-3" style={{ color: accent }} />
                  </span>
                  <span className="flex-1 text-[10px] font-bold text-neutral-900">{p.label}</span>
                  <span className="text-[9px] text-neutral-500">{p.loc}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* iPhone bottom gesture bar */}
          {!isAndroid && (
            <div className="absolute inset-x-0 bottom-[6px] z-30 flex justify-center">
              <span className="h-[4px] w-24 rounded-full bg-white/80" />
            </div>
          )}
          {/* Android nav gesture pill (Material You) */}
          {isAndroid && (
            <div className="absolute inset-x-0 bottom-[6px] z-30 flex justify-center">
              <span className="h-[4px] w-28 rounded-full bg-neutral-500/70" />
            </div>
          )}
        </div>
      </div>

      {/* Realistic side buttons */}
      {!isAndroid ? (
        <>
          <span className="absolute -left-[3px] top-24 h-8 w-[3px] rounded-r bg-gradient-to-b from-neutral-600 to-neutral-800" />
          <span className="absolute -left-[3px] top-36 h-12 w-[3px] rounded-r bg-gradient-to-b from-neutral-600 to-neutral-800" />
          <span className="absolute -left-[3px] top-52 h-12 w-[3px] rounded-r bg-gradient-to-b from-neutral-600 to-neutral-800" />
          <span className="absolute -right-[3px] top-32 h-20 w-[3px] rounded-l bg-gradient-to-b from-neutral-600 to-neutral-800" />
        </>
      ) : (
        <>
          {/* Pixel power button (upper-right, slightly longer) + volume rocker below */}
          <span className="absolute -right-[2px] top-28 h-14 w-[2.5px] rounded-l bg-gradient-to-b from-neutral-500 to-neutral-800" />
          <span className="absolute -right-[2px] top-48 h-20 w-[2.5px] rounded-l bg-gradient-to-b from-neutral-500 to-neutral-800" />
          {/* Subtle camera visor hint at upper-left back (shows as a shadowed strip) */}
          <span className="pointer-events-none absolute -left-[2px] top-24 h-10 w-[2.5px] rounded-r bg-gradient-to-b from-neutral-700 to-neutral-900 opacity-80" />
        </>
      )}
    </div>
  );
};

const Tracker = () => (
  <motion.div
    className="relative flex h-32 w-32 items-center justify-center sm:h-40 sm:w-40"
    initial={{ opacity: 0, x: -24, rotate: -8 }}
    animate={{ opacity: 1, x: 0, rotate: 0 }}
    transition={{ duration: 0.9, type: "spring", stiffness: 160, damping: 22 }}
  >
    {/* Soft brand-tinted glow */}
    <span
      className="absolute inset-0 rounded-[2.5rem] blur-3xl"
      style={{ background: "var(--brand-accent-soft)" }}
    />
    <motion.img
      src={IMAGES.puckMacro}
      alt="Tracksy magnetic tracker in matte black"
      loading="eager"
      className="relative h-full w-full object-contain drop-shadow-[0_22px_32px_rgba(0,0,0,0.28)]"
      animate={{ y: [0, -6, 0], rotate: [0, 2, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Magnetic attraction lines flowing toward the phone */}
    <motion.span
      aria-hidden
      className="absolute right-[-20px] top-1/2 h-[2px] w-6 -translate-y-1/2 rounded-full"
      style={{ background: "var(--brand-accent)" }}
      animate={{ opacity: [0.3, 0.9, 0.3], x: [0, 8, 0] }}
      transition={{ duration: 1.6, repeat: Infinity }}
    />
    <motion.span
      aria-hidden
      className="absolute right-[-28px] top-[42%] h-[2px] w-4 rounded-full"
      style={{ background: "var(--brand-accent)" }}
      animate={{ opacity: [0.2, 0.7, 0.2], x: [0, 5, 0] }}
      transition={{ duration: 1.6, repeat: Infinity, delay: 0.3 }}
    />
    <motion.span
      aria-hidden
      className="absolute right-[-26px] top-[58%] h-[2px] w-5 rounded-full"
      style={{ background: "var(--brand-accent)" }}
      animate={{ opacity: [0.2, 0.8, 0.2], x: [0, 6, 0] }}
      transition={{ duration: 1.6, repeat: Infinity, delay: 0.6 }}
    />
  </motion.div>
);

const HeroVisual = () => {
  const { platform } = usePlatform();
  return (
    <div data-testid="hero-visual" className="relative mx-auto flex max-w-md items-center justify-center gap-2 sm:gap-4">
      <Tracker />
      <AnimatePresence mode="wait">
        <motion.div
          key={platform}
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.98 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <Phone platform={platform} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default HeroVisual;
